import React from 'react';
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {useAuth} from "./auth/AuthProvider";
import { apiClient } from "../services/apiClient";
import { CgDarkMode } from "react-icons/cg";
import {useTheme} from "@heroui/use-theme";
import { Icon } from "@iconify/react";

import {
    Navbar,
    NavbarBrand,
    NavbarContent,
    NavbarItem,
    Button,
    NavbarMenuToggle,
    NavbarMenuItem,
    NavbarMenu,
    Link as NextUILink,
    DropdownTrigger,
    Avatar,
    Dropdown,
    DropdownMenu, DropdownItem, Image, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Tabs, Tab, Select, SelectItem, Chip, Autocomplete, AutocompleteItem
} from "@heroui/react";

export const AcmeLogo = () => {
    return (
        <svg fill="none" height="36" viewBox="0 0 32 32" width="36">
            <path
                clipRule="evenodd"
                d="M17.6482 10.1305L15.8785 7.02583L7.02979 22.5499H10.5278L17.6482 10.1305ZM19.8798 14.0457L18.11 17.1983L19.394 19.4511H16.8453L15.1056 22.5499H24.7272L19.8798 14.0457Z"
                fill="currentColor"
                fillRule="evenodd"
            />
        </svg>
    );
};

export const RSKLogo = () => {
    return (
        <img src={process.env.PUBLIC_URL + "/QuickViewR.png"} alt="Acme Logo" width="150" height="40" />
    )
}

const NavBar = ({ darkMode, setDarkMode, selectedMenu, setSelectedMenu }) => {
    const navigate = useNavigate();
    const { tokenExists, logout, userProfile, loadingProfile } = useAuth();
    const { theme, setTheme } = useTheme()
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = React.useState(false);
    const [isAdminModalOpen, setIsAdminModalOpen] = React.useState(false);
    const [adminData, setAdminData] = React.useState({ privileges: [], availableUsers: [], allowedPermissions: [], financeApprovedUsers: [] });
    const [adminLoading, setAdminLoading] = React.useState(false);
    const [adminError, setAdminError] = React.useState("");
    const [newPermission, setNewPermission] = React.useState({ email: "", permission: "admin" });
    const [notificationEmail, setNotificationEmail] = React.useState("");

    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
        setTheme(darkMode ? 'light' : 'dark'); // Toggle theme based on darkMode state

        console.log("userProfile", userProfile)
    };

    const handleLoginOutBtn = () => {
        if (tokenExists()) {
            logout();
        } else {
            navigate("/login");
        }
    }

    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const openProfileModal = () => setIsProfileModalOpen(true);
    const openAdminModal = () => setIsAdminModalOpen(true);

    const fetchAdminData = async () => {
        try {
            setAdminError("");
            setAdminLoading(true);
            const response = await apiClient.get("/user/admin/overview");
            const data = response.data || {};
            setAdminData({
                privileges: data.privileges || [],
                availableUsers: data.availableUsers || [],
                allowedPermissions: data.allowedPermissions || ["admin", "jefejudicial", "claimsread", "aprobadorfinanciero"],
                financeApprovedUsers: data.financeApprovedUsers || []
            });

            const defaultEmail = data.availableUsers?.[0]?.email || "";
            const defaultPermission = (data.allowedPermissions && data.allowedPermissions[0]) || "admin";
            setNewPermission({
                email: defaultEmail,
                permission: defaultPermission
            });
        } catch (error) {
            console.error("Error obteniendo datos de admin:", error);
            setAdminError("Failed to load admin data");
        } finally {
            setAdminLoading(false);
        }
    };

    const handleAssignPermissionCustom = async (email, permission) => {
        try {
            setAdminError("");
            setAdminLoading(true);
            await apiClient.post("/user/admin/privileges", { email, permission });
            await fetchAdminData();
        } catch (error) {
            console.error("Error asignando permiso:", error);
            const message = error.response?.data?.message || "Failed to assign permission";
            setAdminError(message);
        } finally {
            setAdminLoading(false);
        }
    };
    const handleAssignPermission = async () => {
        if (!newPermission.email || !newPermission.permission) {
            setAdminError("Email and permission are required");
            return;
        }
        await handleAssignPermissionCustom(newPermission.email, newPermission.permission);
    };

    const handleAssignNotification = async () => {
        if (!notificationEmail) return;
        try {
            setAdminError("");
            setAdminLoading(true);
            await apiClient.post("/user/admin/notifications", { email: notificationEmail, type: 'finance_approve' });
            setNotificationEmail("");
            await fetchAdminData();
        } catch (error) {
            console.error("Error asignando notificacion:", error);
            const message = error.response?.data?.message || "Failed to assign notification";
            setAdminError(message);
        } finally {
            setAdminLoading(false);
        }
    };

    const handleRemoveNotification = async (email) => {
        try {
            setAdminError("");
            setAdminLoading(true);
            await apiClient.delete("/user/admin/notifications", {
                data: { email, type: 'finance_approve' }
            });
            await fetchAdminData();
        } catch (error) {
            console.error("Error removiendo notificacion:", error);
            const message = error.response?.data?.message || "Failed to remove notification";
            setAdminError(message);
        } finally {
            setAdminLoading(false);
        }
    };

    const handleRemovePermission = async (email, permission) => {
        try {
            setAdminError("");
            setAdminLoading(true);
            await apiClient.delete("/user/admin/privileges", {
                data: { email, permission }
            });
            await fetchAdminData();
        } catch (error) {
            console.error("Error removiendo permiso:", error);
            const message = error.response?.data?.message || "Failed to remove permission";
            setAdminError(message);
        } finally {
            setAdminLoading(false);
        }
    };

    React.useEffect(() => {
        if (isAdminModalOpen) {
            fetchAdminData();
        }
    }, [isAdminModalOpen]);

    const getUserInfoByEmail = (email) => {
        const found = adminData.availableUsers.find((u) => u.email === email);
        return found || { full_name: email, email, profile: "" };
    };

    const permissions = userProfile?.permissions || [];
    const hasLimitedClaims = permissions.some((p) => p === "claimsread" || p === "aprobadorfinanciero");
    const hasElevatedPermissions = permissions.some((p) => p === "admin" || p === "jefejudicial");
    const restrictClaimsOnly = hasLimitedClaims && !hasElevatedPermissions;

    const baseMenuItems = [
        { name: "Claims", href: "/", mainMenu: true },
        { name: "Maestra", href: "/maestra", mainMenu: true},
    ];

    const menuItems = restrictClaimsOnly
        ? baseMenuItems.filter((item) => item.name === "Claims")
        : baseMenuItems;

    return (
        <Navbar onMenuOpenChange={setIsMenuOpen}>
            <NavbarMenuToggle
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                className="sm:hidden"
            />
            <NavbarBrand>
                <div className="flex gap-1 justify-center items-center" >
                    <RSKLogo />
                    <Image
                        src= {process.env.PUBLIC_URL + "/PoweredBy.png"}
                        className="max-w-[55px] max-h-[30px] object-contain"
                        width={55}
                        height={30}
                        alt="AWS Logo"
                    />
                </div>
            </NavbarBrand>

            <NavbarContent className="hidden sm:flex gap-4" justify="center">
            {menuItems.map((item, index) => (
                <NavbarItem key={index} isActive={selectedMenu === item.name} hidden={!item.mainMenu}>
                    <NextUILink
                        as={RouterLink}
                        color={ selectedMenu === item.name ? "primary" : "foreground" }
                        to={item.href}
                    >
                        {item.name}
                    </NextUILink>
                </NavbarItem>
            ))}
            </NavbarContent>

            <NavbarContent justify="end">
                {/* <NavbarItem>
                    <Button
                        color={tokenExists() ? "secondary" : "primary"}
                        variant="light"
                        onPress={handleLoginOutBtn}
                    >
                        {tokenExists() ? "Logout" : "Login"}
                    </Button>
                </NavbarItem> */}
                <NavbarItem>
                    <Button
                        isIconOnly
                        onPress={toggleDarkMode}
                        aria-label="Toggle Dark Mode"
                        className="transition-all duration-300 hover:scale-110"
                        style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}
                    >
                        <Icon
                            icon={theme === "light" ? "solar:moon-stars-bold-duotone" : "solar:sun-2-bold-duotone"}
                            width={28}
                            height={28}
                            className={theme === "light" ? "text-blue-700" : "text-yellow-400"}
                        />
                    </Button>
                </NavbarItem>


                <Dropdown placement="bottom-end">
                    <DropdownTrigger>
                        <Avatar
                            isBordered
                            as="button"
                            className="transition-transform"
                            color="secondary"
                            name={
                                loadingProfile
                                    ? "Loading..."
                                    : userProfile?.first_name && userProfile?.last_name
                                        ? `${userProfile.first_name.charAt(0).toUpperCase()}${userProfile.last_name.charAt(0).toUpperCase()}` // Mostrar la inicial del nombre completo
                                        : "N/A"
                            }
                            size="sm"
                            src={userProfile?.avatar} // Usar avatar si está disponible
                        />
                    </DropdownTrigger>
                    <DropdownMenu aria-label="Profile Actions" variant="flat">
                        <DropdownItem key="profile" className="h-14 gap-2">
                            <p className="font-semibold">
                                {loadingProfile
                                    ? "Loading profile..."
                                    : userProfile
                                        ? `Signed in as ${userProfile.name}`
                                        : "Not signed in"}
                            </p>
                            <p className="font-semibold">
                                {userProfile?.email || "no email available"}
                            </p>
                        </DropdownItem>
                        <DropdownItem key="profile-modal" onPress={openProfileModal}>My Profile</DropdownItem>
                        {userProfile?.permissions?.includes("admin") && (
                            <DropdownItem key="admin-modal" onPress={openAdminModal}>Config</DropdownItem>
                        )}
                        {tokenExists() && (
                            <DropdownItem
                                key="logout"
                                color="danger"
                                onPress={logout}
                                startContent={<Icon icon="mdi:logout" width="16" height="16" />}
                            >
                                Log Out
                            </DropdownItem>
                        )}
                        {process.env.REACT_APP_VERSION && (
                            <DropdownItem
                                key="app-version"
                                isDisabled
                                className="text-xs text-default-400"
                            >
                                Version: {process.env.REACT_APP_VERSION}
                            </DropdownItem>
                        )}
                    </DropdownMenu>
                </Dropdown>
            </NavbarContent>

            <Modal isOpen={isProfileModalOpen} onOpenChange={setIsProfileModalOpen}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">My Profile</ModalHeader>
                            <ModalBody>
                                <p><span className="font-semibold">Name:</span> {userProfile?.name || "N/A"}</p>
                                <p><span className="font-semibold">Email:</span> {userProfile?.email || "N/A"}</p>
                                <div>
                                    <p className="font-semibold">Permissions</p>
                                    {loadingProfile ? (
                                        <p>Loading profile...</p>
                                    ) : userProfile?.permissions?.length ? (
                                        <ul className="list-disc pl-4">
                                            {userProfile.permissions.map((permission) => (
                                                <li key={permission}>{permission}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p>No permissions available</p>
                                    )}
                                </div>
                            </ModalBody>
                            <ModalFooter>
                                <Button color="primary" onPress={onClose}>
                                    Close
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            <Modal isOpen={isAdminModalOpen} onOpenChange={setIsAdminModalOpen} size="xl">
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">Config</ModalHeader>
                            <ModalBody className="max-h-[70vh] overflow-y-auto">
                                {adminError && (
                                    <p className="text-danger text-sm">{adminError}</p>
                                )}
                                <Tabs aria-label="Config Options">
                                    <Tab key="users" title="Manage Users">
                                        <div className="flex flex-col gap-4 mt-2">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                                                <div className="flex flex-col">
                                                    <label className="text-sm font-semibold mb-1">User</label>
                                                    <select
                                                        className="border rounded-md px-2 py-1 text-sm"
                                                        value={newPermission.email}
                                                        onChange={(e) => setNewPermission((prev) => ({ ...prev, email: e.target.value }))}
                                                        disabled={adminLoading}
                                                    >
                                                        <option value="">Select user</option>
                                                        {adminData.availableUsers.map((user) => (
                                                            <option key={user.email} value={user.email}>
                                                                {user.full_name} - {user.email}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="flex flex-col">
                                                    <label className="text-sm font-semibold mb-1">Permission</label>
                                                    <select
                                                        className="border rounded-md px-2 py-1 text-sm"
                                                        value={newPermission.permission}
                                                        onChange={(e) => setNewPermission((prev) => ({ ...prev, permission: e.target.value }))}
                                                        disabled={adminLoading}
                                                    >
                                                        {adminData.allowedPermissions.length === 0 && (
                                                            <option value="">No permissions</option>
                                                        )}
                                                        {adminData.allowedPermissions.map((perm) => (
                                                            <option key={perm} value={perm}>{perm}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="flex flex-col">
                                                    <Button
                                                        color="primary"
                                                        onPress={handleAssignPermission}
                                                        isDisabled={adminLoading}
                                                    >
                                                        Assign Permission
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="flex flex-col gap-2">
                                                <p className="font-semibold">Current permissions</p>
                                                {adminLoading ? (
                                                    <p>Loading...</p>
                                                ) : adminData.privileges.length === 0 ? (
                                                    <p>No users with permissions yet.</p>
                                                ) : (
                                                    <ul className="space-y-2">
                                                        {adminData.privileges.map(({ email, permission }) => {
                                                            const info = getUserInfoByEmail(email);
                                                            return (
                                                                <li key={`${email}-${permission}`} className="flex items-center justify-between border rounded-md px-3 py-2">
                                                                    <div>
                                                                        <p className="font-semibold">{info.full_name || email}</p>
                                                                        <p className="text-sm text-default-500">{email}</p>
                                                                        {info.profile && <p className="text-sm">Profile: {info.profile}</p>}
                                                                        <p className="text-sm">Permission: {permission}</p>
                                                                    </div>
                                                                    <Button
                                                                        size="sm"
                                                                        color="danger"
                                                                        variant="flat"
                                                                        onPress={() => handleRemovePermission(email, permission)}
                                                                        isDisabled={adminLoading}
                                                                    >
                                                                        Remove
                                                                    </Button>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                )}
                                            </div>
                                        </div>
                                    </Tab>
                                    <Tab key="notifications" title="Manage Notifications">
                                        <div className="flex flex-col gap-4 mt-2">
                                            <p className="text-sm text-default-500">
                                                Manage users who receive the "Finance Approved" email notification.
                                            </p>
                                            <div className="flex flex-col gap-2">
                                                <label className="text-sm font-semibold">Add user</label>
                                                <div className="flex items-center gap-2">
                                                    <Autocomplete
                                                        allowsCustomValue
                                                        label="Search user or type custom email"
                                                        variant="bordered"
                                                        defaultItems={adminData.availableUsers}
                                                        inputValue={notificationEmail}
                                                        onInputChange={(value) => setNotificationEmail(value)}
                                                        onSelectionChange={(key) => {
                                                            if (key) setNotificationEmail(key);
                                                        }}
                                                        isDisabled={adminLoading}
                                                        className="flex-1"
                                                    >
                                                        {(item) => <AutocompleteItem key={item.email} textValue={item.email}>{item.full_name} - {item.email}</AutocompleteItem>}
                                                    </Autocomplete>
                                                    <Button
                                                        color="primary"
                                                        onPress={handleAssignNotification}
                                                        isDisabled={adminLoading || !notificationEmail}
                                                        className="h-14"
                                                    >
                                                        Add
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {adminData.financeApprovedUsers.map(email => {
                                                    const info = getUserInfoByEmail(email);
                                                    return (
                                                        <Chip
                                                            key={email}
                                                            onClose={() => handleRemoveNotification(email)}
                                                            variant="flat"
                                                            color="primary"
                                                            isDisabled={adminLoading}
                                                        >
                                                            {info.full_name && info.full_name !== email ? `${info.full_name} (${email})` : email}
                                                        </Chip>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </Tab>
                                </Tabs>
                            </ModalBody>
                            <ModalFooter>
                                <Button color="primary" variant="flat" onPress={onClose}>
                                    Close
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            <NavbarMenu>
                {menuItems.map((item, index) => (
                    <NavbarMenuItem key={`${item}-${index}`} isActive={selectedMenu === item.name}>
                        <NextUILink
                            as={RouterLink}
                            color={
                                index === 2 ? "primary" : index === menuItems.length - 1 ? "danger" : "foreground"
                            }
                            to={item.href}>
                            {item.name}
                        </NextUILink>
                    </NavbarMenuItem>
                ))}
            </NavbarMenu>
        </Navbar>
    );
}

export default NavBar
